import React, { createContext, useContext, useState } from "react";

type DrawerContextProps = {
  isAnyDrawerOpen: boolean;
  setIsAnyDrawerOpen: (open: boolean) => void;
};

const DrawerContext = createContext<DrawerContextProps | undefined>(undefined);

/**
 * Provides context for managing the open state of any drawer in the application.
 *
 * @param children - The React node(s) that will have access to the drawer context.
 * @returns A context provider that supplies `isAnyDrawerOpen` and `setIsAnyDrawerOpen`
 *          to its descendants.
 *
 * @example
 * ```tsx
 * <DrawerProvider>
 *   <YourComponent />
 * </DrawerProvider>
 * ```
 */
export const DrawerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAnyDrawerOpen, setIsAnyDrawerOpen] = useState(false);

  return (
    <DrawerContext.Provider value={{ isAnyDrawerOpen, setIsAnyDrawerOpen }}>
      {children}
    </DrawerContext.Provider>
  );
};

export const useDrawer = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error("useDrawer must be used within a DrawerProvider");
  }
  return context;
};
